import { IncidentSeverity } from './incidents';
import type { Incident } from './incidents';

/**
 * Notification payload published via SignalR when incidents trigger notifications.
 */
export interface NotificationPayload {
  /** The incident that triggered the notification */
  incident: Incident;
  /** Reason for notification: Created, SlaWarning, or SlaBreached */
  reason: 'Created' | 'SlaWarning' | 'SlaBreached';
  /** The notification rule that was evaluated */
  rule: NotificationRule;
}

/**
 * Notification channels that can be enabled for an incident.
 */
export interface NotificationChannels {
  /** Display alert in Status Panel UI */
  statusPanel: boolean;
  /** Send email notification via SMTP */
  email: boolean;
  /** Send Teams message (DM or channel) */
  teams: boolean;
  /** Play audio alert in browser */
  audio: boolean;
}

/**
 * Notification rule for a specific severity level.
 */
export interface NotificationRule {
  /** The incident severity this rule applies to */
  severity: IncidentSeverity;
  /** Which notification channels are enabled */
  channels: NotificationChannels;
  /** Specific customer ID or null for global rule */
  customerId: string | null;
  /** Whether to send notification when the Acknowledge SLA deadline has been exceeded */
  notifyOnAckSlaExceeded: boolean;
  /** Whether to send notification when the Update SLA deadline has been exceeded */
  notifyOnUpdateSlaExceeded: boolean;
  /** Whether to send notification when SLA is breached */
  notifyOnSlaBreached: boolean;
  /** Group IDs to notify */
  notificationGroupIds: string[];
  /** Manual list of email addresses/UPNs */
  notificationEmails: string[];
  /** Whether to notify admin group for unowned incidents */
  notifyAdminGroupForUnowned: boolean;
}

/**
 * SOC role for group members.
 */
export enum SocRole {
  Operator = 'Operator',
  Admin = 'Admin',
}

/**
 * Configured security group for notification routing.
 */
export interface GroupConfig {
  /** Azure AD Group ID */
  groupId: string;
  /** Display name (e.g., "SOC Operators") */
  displayName: string;
  /** Roles assigned to members */
  assignedRoles: SocRole[];
  /** Cached member count */
  cachedMemberCount: number;
  /** When cache was last updated */
  cachedMemberCountTime: string | null;
}

/**
 * User information from group membership.
 */
export interface GroupMember {
  /** Azure AD User ID */
  userId: string;
  /** Display name */
  displayName: string;
  /** Email / UPN */
  email: string;
}

/**
 * Azure AD group visible to the current user via delegated Graph API.
 */
export interface DiscoverableGroup {
  /** Azure AD Group Object ID */
  groupId: string;
  /** Group display name */
  displayName: string;
  /** Optional description */
  description?: string | null;
}

/**
 * Notification capabilities reported by the API.
 * Indicates which notification channels are configured server-side.
 */
export interface NotificationCapabilities {
  /** Whether email notifications are enabled (Graph API configured) */
  emailEnabled: boolean;
  /** Whether Teams channel notifications are enabled (webhook URL configured) */
  teamsEnabled: boolean;
  /** Whether Teams direct messages are enabled (OBO token configured) */
  teamsDirectMessageEnabled: boolean;
}

/**
 * Default notification channels (all disabled).
 */
export const createDefaultChannels = (): NotificationChannels => ({
  statusPanel: true,
  email: false,
  teams: false,
  audio: false,
});

/**
 * Creates default rule for a severity.
 */
export const createDefaultRule = (severity: IncidentSeverity): NotificationRule => ({
  severity,
  channels: createDefaultChannels(),
  customerId: null,
  notifyOnAckSlaExceeded: true,
  notifyOnUpdateSlaExceeded: true,
  notifyOnSlaBreached: true,
  notificationGroupIds: [],
  notificationEmails: [],
  notifyAdminGroupForUnowned: true,
});
